import * as am4charts from "@amcharts/amcharts4/charts";
import * as am4core from "@amcharts/amcharts4/core";
import { forwardRef, useEffect } from "react";
import { applyAppFontFamily, configureNumberFormatter } from "../utils";
import { WinLossStackedColumnChartProps } from "./types.js";
import {
  configureWinLossChartPadding,
  createLossSeries,
  createWinLossCategoryAxis,
  createWinLossLegend,
  createWinLossPercentageAxis,
  createWinSeries,
} from "./utils.js";

/**
 * Win/Loss Stacked Column Chart component
 * Displays stacked columns with win and loss segments that total 100%
 */
export const WinLossStackedColumnChart = forwardRef<
  am4charts.XYChart,
  WinLossStackedColumnChartProps
>((props, ref) => {
  const {
    id,
    data,
    height = "300px",
    winColor,
    lossColor,
    showLegend = true,
    legendPosition = "bottom",
  } = props;

  useEffect(() => {
    const chart = am4core.create(id, am4charts.XYChart);

    if (typeof ref === "function") {
      ref(chart);
    } else if (ref) {
      ref.current = chart;
    }

    chart.data = data;
    configureNumberFormatter(chart);
    configureWinLossChartPadding(chart);

    createWinLossCategoryAxis(chart);
    createWinLossPercentageAxis(chart);
    createWinSeries(chart, winColor);
    createLossSeries(chart, lossColor);

    if (showLegend) {
      createWinLossLegend(chart, legendPosition);
    }

    applyAppFontFamily(chart);

    return () => {
      chart.dispose();
    };
  }, [data, id, ref, winColor, lossColor, showLegend, legendPosition]);

  return (
    <div
      id={id}
      style={{
        width: "100%",
        height,
      }}
    />
  );
});

WinLossStackedColumnChart.displayName = "WinLossStackedColumnChart";
